import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, ChevronRight, Ticket, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import DemoModeBadge from '../components/DemoModeBadge';

/**
 * Associate landing page — lists every event the current user can manage.
 * Owners + admins also see this surface so the route can be used as a
 * unified "events I can manage" view.
 */
const AssociateEventsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/associate/events`, { withCredentials: true });
        if (active) setRows(res.data?.events || []);
      } catch {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="min-h-[80vh] bg-stone-50 py-12 px-4">
      <div className="max-w-3xl mx-auto" data-testid="associate-events-page">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] mb-1">Associate Tools</p>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="font-serif text-4xl text-[#1F3D2B]">My events</h1>
            <DemoModeBadge />
          </div>
          <p className="text-sm text-stone-500">
            {user?.role === 'associate'
              ? "Events you've been invited to help manage."
              : 'Events you can manage.'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#1F3D2B]" />
          </div>
        ) : rows.length === 0 ? (
          <div
            className="bg-white border border-stone-200 rounded-2xl shadow-sm p-12 text-center"
            data-testid="associate-events-empty"
          >
            <Ticket className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="font-serif text-xl text-[#1F3D2B] mb-2">No events yet.</p>
            <p className="text-sm text-stone-500">
              Once an organiser invites you to manage an event, it will appear here.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl shadow-sm divide-y divide-stone-100" data-testid="associate-events-list">
            {rows.map(({ event, permissions }) => (
              <Link
                key={event.event_id}
                to={`/associate/events/${event.event_id}/tools`}
                className="flex items-center gap-4 p-5 hover:bg-stone-50 transition-colors"
                data-testid={`associate-event-row-${event.event_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-[#1F3D2B] truncate">{event.title}</p>
                  <p className="text-xs text-[#4A5D50] mt-0.5 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {event.date}{event.time ? ` · ${event.time}` : ''}
                  </p>
                  {event.location && (
                    <p className="text-xs text-[#4A5D50] mt-0.5 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {event.location}
                    </p>
                  )}
                  {permissions && (
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 mt-2">
                      {Object.values(permissions).filter(Boolean).length} permission(s) granted
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssociateEventsPage;
